import { invoke } from '@tauri-apps/api/core';
import { isTauri } from './tauri';
import {
  generateCodeVerifier,
  computeCodeChallengeS256,
  generateOAuthState,
  buildAuthorizeUrl,
  defaultRedirectUri,
  expiresAtFromExpiresIn,
} from './oauthPkce';

/** The fields a token exchange sends — the ones a user writes as `{{clientId}}`. */
const TEMPLATE_FIELDS = ['authUrl', 'tokenUrl', 'clientId', 'clientSecret', 'scope', 'redirectUri'];

/**
 * A copy of the OAuth2 config with `{{variables}}` resolved in the fields the
 * exchange sends. The stored config keeps its `{{…}}` text: only the outgoing
 * call is resolved, as every other auth field is at send time. Without this the
 * token endpoint received `client_id={{clientId}}` verbatim (16.10.2026).
 * Tokens are not touched — they come from the server.
 */
export function resolveOAuthConfig(oauth2Config, substituteVariables) {
  if (!oauth2Config || typeof substituteVariables !== 'function') return oauth2Config;
  const out = { ...oauth2Config };
  for (const field of TEMPLATE_FIELDS) {
    if (typeof out[field] === 'string') out[field] = substituteVariables(out[field]);
  }
  return out;
}

export async function runOAuthPkceFlow(storedConfig, { substituteVariables } = {}) {
  if (!isTauri()) {
    throw new Error('OAUTH_DESKTOP_ONLY');
  }
  const oauth2Config = resolveOAuthConfig(storedConfig, substituteVariables);

  const verifier = generateCodeVerifier();
  const challenge = await computeCodeChallengeS256(verifier);
  const state = generateOAuthState();

  const port = await invoke('oauth_bind_callback', { expectedState: state });
  const redirectUri = oauth2Config.redirectUri || defaultRedirectUri(port);

  const authorizeUrl = buildAuthorizeUrl({
    authUrl: oauth2Config.authUrl,
    clientId: oauth2Config.clientId,
    redirectUri,
    scope: oauth2Config.scope,
    state,
    codeChallenge: challenge,
  });

  const { open } = await import('@tauri-apps/plugin-shell');
  try {
    await open(authorizeUrl);
  } catch (err) {
    // Release the bound TCP listener so future OAuth attempts can succeed
    await invoke('oauth_cancel_callback').catch(() => {});
    throw err;
  }

  const callback = await invoke('oauth_await_callback');

  const token = await invoke('oauth_exchange_token', {
    request: {
      token_url: oauth2Config.tokenUrl,
      client_id: oauth2Config.clientId,
      client_secret: oauth2Config.clientSecret || null,
      code: callback.code,
      redirect_uri: redirectUri,
      code_verifier: verifier,
    },
  });

  return {
    accessToken: token.access_token,
    refreshToken: token.refresh_token || '',
    tokenType: token.token_type || 'Bearer',
    expiresAt: expiresAtFromExpiresIn(token.expires_in),
    // The user's own value stays as typed (a `{{redirectUri}}` is not replaced
    // by what it resolved to); only the generated loopback default is new.
    redirectUri: storedConfig.redirectUri || redirectUri,
  };
}

export async function refreshOAuthToken(storedConfig, { substituteVariables } = {}) {
  if (!isTauri()) {
    throw new Error('OAUTH_DESKTOP_ONLY');
  }
  const oauth2Config = resolveOAuthConfig(storedConfig, substituteVariables);
  if (!oauth2Config.refreshToken) {
    throw new Error('No refresh token available');
  }

  const token = await invoke('oauth_refresh_token', {
    request: {
      token_url: oauth2Config.tokenUrl,
      client_id: oauth2Config.clientId,
      client_secret: oauth2Config.clientSecret || null,
      refresh_token: oauth2Config.refreshToken,
    },
  });

  return {
    accessToken: token.access_token,
    refreshToken: token.refresh_token || oauth2Config.refreshToken,
    tokenType: token.token_type || oauth2Config.tokenType || 'Bearer',
    expiresAt: expiresAtFromExpiresIn(token.expires_in),
  };
}

export async function runOAuthClientCredentials(storedConfig, { substituteVariables } = {}) {
  const oauth2Config = resolveOAuthConfig(storedConfig, substituteVariables);
  if (!oauth2Config.tokenUrl) throw new Error('Token URL is required');
  if (!oauth2Config.clientId) throw new Error('Client ID is required');

  const token = await invoke('oauth_client_credentials', {
    request: {
      token_url: oauth2Config.tokenUrl,
      client_id: oauth2Config.clientId,
      client_secret: oauth2Config.clientSecret || null,
      scope: oauth2Config.scope || null,
    },
  });

  return {
    accessToken: token.access_token,
    refreshToken: token.refresh_token || '',
    tokenType: token.token_type || 'Bearer',
    expiresAt: expiresAtFromExpiresIn(token.expires_in),
  };
}

export function isOAuthTokenExpired(oauth2) {
  if (!oauth2?.expiresAt) return false;
  return Date.now() >= Number(oauth2.expiresAt) - 30_000;
}
